import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { listAccessForUser, type DigitalAccessEntry } from '@/lib/digitalAccess';
import type { ZifekUser } from '@/hooks/useAuth';

interface OrderItemJson {
  name?: string;
  quantity?: number;
  price?: number;
}

interface OrderRow {
  id: number;
  titre: string | null;
  date_time: string | null;
  totalttc: string | null;
  sp: string | null;
  sommepayee: string | null;
  monaie: string | null;
  quantite: number | null;
  details: string | null;
  items_json: OrderItemJson[] | null;
  statut_com_client: string | null;
  statut_com_vendeur: string | null;
  methodepayment: string | null;
  adresse: string | null;
  ville: string | null;
  idvendeur: number | null;
}

interface DigitalFile {
  productId: number;
  name: string;
  url: string | null;
  fileName: string | null;
}

type Filter = 'all' | 'paid' | 'pending';

const PAID_STATUSES = [
  'paye', 'payee', 'payé', 'payée', 'confirmee', 'confirmée', 'confirme',
  'preparee', 'préparation', 'preparation', 'expediee', 'expédiée',
  'livree', 'livrée', 'terminee', 'terminée', 'complete', 'complète',
];

function normalizeStatus(value: string | null): string {
  return (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function isPaidOrder(order: OrderRow): boolean {
  const paidAmount = Number(order.sommepayee);
  if (Number.isFinite(paidAmount) && paidAmount > 0) return true;
  return (
    PAID_STATUSES.includes(normalizeStatus(order.statut_com_vendeur)) ||
    PAID_STATUSES.includes(normalizeStatus(order.statut_com_client))
  );
}

function fmtDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

function money(value: number, currency: string): string {
  return `${value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency || ''}`.trim();
}

interface Props {
  user: ZifekUser;
}

/**
 * « Mes commandes » — historique complet des achats du client.
 *
 * Pour chaque commande : statut réel (payé / en attente), articles, total et
 * surtout les FICHIERS DIGITAUX achetés, téléchargeables directement ici dès
 * que le paiement est confirmé.
 */
export default function OrdersTab({ user }: Props) {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [digitalByOrder, setDigitalByOrder] = useState<Record<number, DigitalFile[]>>({});
  const [orphanFiles, setOrphanFiles] = useState<DigitalFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);

    try {
      // Rapprochement de la commande avec le compte connecté :
      // par identifiant, par email ou par téléphone.
      const filters: string[] = [`user_id.eq.${user.id}`];
      if (user.email) {
        filters.push(`email.eq.${user.email}`);
        filters.push(`user_id.eq.${user.email}`);
      }
      if (user.telephone) {
        filters.push(`tel.eq.${user.telephone}`);
        filters.push(`user_id.eq.${user.telephone}`);
      }

      const { data, error: readError } = await supabase
        .from('commande')
        .select('id, titre, date_time, totalttc, sp, sommepayee, monaie, quantite, details, items_json, statut_com_client, statut_com_vendeur, methodepayment, adresse, ville, idvendeur')
        .or(filters.join(','))
        .order('date_time', { ascending: false })
        .limit(80);

      if (readError) throw readError;
      const rows = (data || []) as OrderRow[];
      setOrders(rows);

      // Fichiers digitaux : accès du client → regroupés par commande.
      let access: DigitalAccessEntry[] = [];
      try {
        access = await listAccessForUser(Number(user.id));
      } catch {
        access = [];
      }

      if (access.length > 0) {
        const productIds = Array.from(new Set(access.map((a) => a.productId)));
        const { data: products } = await supabase
          .from('product_items')
          .select('id, name, digital_file_url, digital_file_name')
          .in('id', productIds);

        const productMap = new Map<number, { name: string; url: string | null; fileName: string | null }>();
        ((products || []) as { id: number; name: string | null; digital_file_url: string | null; digital_file_name: string | null }[])
          .forEach((p) => productMap.set(Number(p.id), {
            name: p.name || `Produit #${p.id}`,
            url: p.digital_file_url || null,
            fileName: p.digital_file_name || null,
          }));

        const grouped: Record<number, DigitalFile[]> = {};
        const orphans: DigitalFile[] = [];
        access.forEach((entry) => {
          const product = productMap.get(entry.productId);
          const file: DigitalFile = {
            productId: entry.productId,
            name: product?.name || entry.productName || `Produit #${entry.productId}`,
            url: product?.url || null,
            fileName: product?.fileName || null,
          };
          if (entry.orderId == null) {
            orphans.push(file);
            return;
          }
          const orderId = Number(entry.orderId);
          if (!grouped[orderId]) grouped[orderId] = [];
          grouped[orderId].push(file);
        });
        setDigitalByOrder(grouped);
        setOrphanFiles(orphans);
      } else {
        setDigitalByOrder({});
        setOrphanFiles([]);
      }
    } catch {
      setError('Impossible de charger vos commandes. Vérifiez votre connexion puis réessayez.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const paid = orders.filter(isPaidOrder);
    const digitalCount = Object.values(digitalByOrder).reduce((sum, list) => sum + list.length, 0)
      + orphanFiles.length;
    return { total: orders.length, paid: paid.length, pending: orders.length - paid.length, digitalCount };
  }, [orders, digitalByOrder, orphanFiles]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((order) => {
      const paid = isPaidOrder(order);
      if (filter === 'paid' && !paid) return false;
      if (filter === 'pending' && paid) return false;
      if (!q) return true;
      const haystack = [
        order.titre,
        order.details,
        order.statut_com_client,
        order.statut_com_vendeur,
        String(order.id),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [orders, filter, search]);

  const filterButtons: { id: Filter; label: string; count: number }[] = [
    { id: 'all', label: 'Toutes', count: stats.total },
    { id: 'paid', label: 'Payées', count: stats.paid },
    { id: 'pending', label: 'En attente', count: stats.pending },
  ];

  return (
    <div className="max-w-4xl xl:max-w-5xl mx-auto">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
            Mes commandes
          </h2>
          <p className="text-xs text-foreground-500 mt-1">
            {stats.total} commande{stats.total !== 1 ? 's' : ''}
            {stats.digitalCount > 0 ? ` · ${stats.digitalCount} fichier${stats.digitalCount !== 1 ? 's' : ''} numérique${stats.digitalCount !== 1 ? 's' : ''}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 self-start"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Actualiser
        </button>
      </div>

      {/* Chiffres clés */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-background-50 border border-background-200/50 rounded-lg p-4">
          <p className="text-lg font-bold font-heading text-foreground-950">{stats.total}</p>
          <p className="text-xs text-foreground-500 mt-0.5">Commandes</p>
        </div>
        <div className="bg-background-50 border border-background-200/50 rounded-lg p-4">
          <p className="text-lg font-bold font-heading text-accent-600">{stats.paid}</p>
          <p className="text-xs text-foreground-500 mt-0.5">Payées</p>
        </div>
        <div className="bg-background-50 border border-background-200/50 rounded-lg p-4">
          <p className="text-lg font-bold font-heading text-foreground-700">{stats.pending}</p>
          <p className="text-xs text-foreground-500 mt-0.5">En attente</p>
        </div>
        <div className="bg-background-50 border border-background-200/50 rounded-lg p-4">
          <p className="text-lg font-bold font-heading text-secondary-700">{stats.digitalCount}</p>
          <p className="text-xs text-foreground-500 mt-0.5">Fichiers numériques</p>
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 text-red-700 text-xs rounded-lg mb-4">
          <i className="ri-error-warning-line"></i>
          <span className="flex-1">{error}</span>
          <button onClick={load} className="cursor-pointer font-semibold whitespace-nowrap hover:text-red-900">
            Réessayer
          </button>
        </div>
      )}

      {/* Filtres + recherche */}
      {orders.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <div className="inline-flex items-center gap-1 px-1 py-1 bg-background-100 rounded-full w-fit">
            {filterButtons.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  filter === f.id
                    ? 'bg-background-50 text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                {f.label}
                <span className="ml-1.5 text-[10px] text-foreground-400">{f.count}</span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-0 max-w-sm">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une commande..."
              className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/40 rounded-lg text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
        </div>
      )}

      {/* Contenu */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/30 rounded-xl">
          <div className="w-14 h-14 rounded-2xl bg-foreground-100 flex items-center justify-center mb-4">
            <i className="ri-shopping-bag-3-line text-xl text-foreground-500"></i>
          </div>
          <h3 className="text-foreground-800 font-semibold text-sm mb-1">
            {orders.length === 0 ? 'Aucune commande pour le moment' : 'Aucun résultat'}
          </h3>
          <p className="text-foreground-400 text-xs text-center max-w-xs mb-4">
            {orders.length === 0
              ? 'Vos achats sur les boutiques Zifek apparaîtront ici, avec leur statut de paiement et vos fichiers.'
              : 'Aucune commande ne correspond à ce filtre ou à cette recherche.'}
          </p>
          {orders.length === 0 && (
            <Link
              to="/"
              className="px-4 py-2.5 rounded-full bg-foreground-900 text-background-50 text-xs font-semibold no-underline hover:bg-foreground-800 transition-colors whitespace-nowrap"
            >
              Découvrir les boutiques
            </Link>
          )}
        </div>
      ) : (
        <>
          {visible.length === 0 && (
            <div className="flex items-center gap-2 px-4 py-3 bg-background-100 text-foreground-600 text-xs rounded-lg mb-3">
              <i className="ri-filter-3-line"></i>
              Aucune commande ne correspond à ce filtre ou à cette recherche.
            </div>
          )}
          <div className="space-y-3">
          {visible.map((order) => {
            const paid = isPaidOrder(order);
            const total = Number(order.totalttc ?? order.sp ?? 0);
            const currency = order.monaie || 'MAD';
            const items = Array.isArray(order.items_json) ? order.items_json : [];
            const files = digitalByOrder[order.id] || [];
            const statusLabel = order.statut_com_vendeur || order.statut_com_client || 'En attente';

            return (
              <article key={order.id} className="bg-background-50 border border-background-200/40 rounded-xl overflow-hidden">
                {/* En-tête de commande */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 border-b border-background-200/40">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-foreground-900 truncate">
                        {order.titre || `Commande #${order.id}`}
                      </p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${
                        paid
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-secondary-100 text-secondary-700'
                      }`}>
                        <i className={paid ? 'ri-checkbox-circle-fill' : 'ri-time-line'}></i>
                        {paid ? 'Payé' : 'Paiement en attente'}
                      </span>
                    </div>
                    <p className="text-[11px] text-foreground-400 mt-0.5">
                      #{order.id} · {fmtDate(order.date_time)}
                      {!paid && statusLabel ? ` · ${statusLabel}` : ''}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0">
                    <p className="text-sm font-bold text-foreground-950">{money(total, currency)}</p>
                    <p className="text-[11px] text-foreground-400">
                      {order.quantite || items.length || 0} article{(order.quantite || items.length || 0) > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {/* Articles */}
                <div className="px-5 py-3">
                  {items.length > 0 ? (
                    <ul className="space-y-1.5">
                      {items.slice(0, 6).map((item, index) => (
                        <li key={`${order.id}-${index}`} className="flex items-center gap-2 text-xs">
                          <i className="ri-arrow-right-s-line text-foreground-300 flex-shrink-0"></i>
                          <span className="text-foreground-700 truncate flex-1">
                            {item.name || 'Article'}
                          </span>
                          <span className="text-foreground-400 whitespace-nowrap">×{item.quantity || 1}</span>
                        </li>
                      ))}
                      {items.length > 6 && (
                        <li className="text-[11px] text-foreground-400 pl-5">+ {items.length - 6} autre(s) article(s)</li>
                      )}
                    </ul>
                  ) : (
                    <p className="text-xs text-foreground-600 line-clamp-2">{order.details || 'Aucun détail disponible.'}</p>
                  )}

                  {(order.methodepayment || order.adresse || order.ville) && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-foreground-400">
                      {order.methodepayment && (
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-bank-card-line"></i>
                          {order.methodepayment}
                        </span>
                      )}
                      {(order.adresse || order.ville) && (
                        <span className="inline-flex items-center gap-1 min-w-0">
                          <i className="ri-map-pin-line"></i>
                          <span className="truncate">{[order.adresse, order.ville].filter(Boolean).join(', ')}</span>
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Fichiers digitaux */}
                {files.length > 0 && (
                  <div className="px-5 py-3 bg-secondary-50 border-t border-secondary-100">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-secondary-700 mb-2 flex items-center gap-1.5">
                      <i className="ri-download-cloud-2-line"></i>
                      Fichiers numériques
                    </p>
                    <div className="space-y-2">
                      {files.map((file) => (
                        <div key={`${order.id}-${file.productId}`} className="flex items-center gap-2.5 bg-background-50 border border-background-200/50 rounded-lg px-3 py-2">
                          <i className="ri-file-line text-primary-500 flex-shrink-0"></i>
                          <span className="text-xs text-foreground-800 truncate flex-1">{file.name}</span>
                          {file.url ? (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-foreground-900 text-background-50 text-[11px] font-semibold no-underline hover:bg-foreground-800 transition-colors whitespace-nowrap"
                            >
                              <i className="ri-download-2-line"></i>
                              Télécharger
                            </a>
                          ) : (
                            <span className="text-[11px] text-foreground-400 whitespace-nowrap">Lien en préparation</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pied de carte */}
                {!paid && (
                  <div className="px-5 py-3 border-t border-background-200/40 flex items-center gap-2 text-[11px] text-foreground-500">
                    <i className="ri-information-line text-secondary-600"></i>
                    Le marchand confirmera le paiement. Vos fichiers numériques se débloquent dès la confirmation.
                  </div>
                )}
              </article>
            );
          })}
          </div>

          {orphanFiles.length > 0 && (
            <section className="mt-4 bg-background-50 border border-background-200/40 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-background-200/40">
                <p className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
                  <i className="ri-download-cloud-2-line text-primary-500"></i>
                  Autres fichiers numériques
                </p>
                <p className="text-[11px] text-foreground-400 mt-0.5">
                  Accès accordés par un marchand, indépendamment d’une commande.
                </p>
              </div>
              <div className="px-5 py-3 space-y-2">
                {orphanFiles.map((file) => (
                  <div key={`orphan-${file.productId}`} className="flex items-center gap-2.5 bg-background-100/60 border border-background-200/50 rounded-lg px-3 py-2">
                    <i className="ri-file-line text-primary-500 flex-shrink-0"></i>
                    <span className="text-xs text-foreground-800 truncate flex-1">{file.name}</span>
                    {file.url ? (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-foreground-900 text-background-50 text-[11px] font-semibold no-underline hover:bg-foreground-800 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-download-2-line"></i>
                        Télécharger
                      </a>
                    ) : (
                      <span className="text-[11px] text-foreground-400 whitespace-nowrap">Lien en préparation</span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}